import { useValidationRules } from "@/iti-core-react/hooks/validation-rule-required-editors";
import { InfoPopOver } from "@/pages/Catalogue/components/InfoPopOver/InfoPopOver";
import DxSelectBox from "devextreme-react/select-box";
import DxValidator from "devextreme-react/validator";
import {
	ContentReadyEvent,
	InitializedEvent,
	SelectionChangedEvent,
	ValueChangedEvent,
} from "devextreme/ui/select_box_types";
import { useMemo } from "react";
import { ItiString } from "../../utils/iti-string";
import "./ItiSelectBox.scss";

interface ItiSelectBoxProps {
	name?: string;
	label?: string;
	labelClass?: string;
	placeholder?: string;
	tabIndex?: number;
	readOnly?: boolean;
	disabled?: boolean;
	visible?: boolean;
	grouped?: boolean;
	validationRules?: any;
	items?: any;
	dataSource?: any;
	displayExpr?: any;
	valueExpr?: string;
	value?: any;
	searchModeOption?: "contains" | "startswith";
	searchExprOption?: string;
	minSearchLengthOption?: number;
	searchEnabled?: boolean;
	showClearButton?: boolean;
	width?: number;
	elementAttr?: any;
	className?: string;
	stylingMode?: "outlined" | "underlined" | "filled";
	deferRendering?: any;
	infoPopOverMessage?: any;
	infoPopOverClassName?: string;
	infoPopOverWidth?: string;
	validationMessageMode?: "always" | "auto";
	sortByProperty?: string;
	sortDirection?: "asc" | "desc";
	onValueChanged?: (e: ValueChangedEvent) => void;
	onSelectionChanged?: (e: SelectionChangedEvent) => void;
	onContentReady?: (e: ContentReadyEvent) => void;
	saveSelectBoxInstance?: (e: InitializedEvent) => void;
}

export default function ItiSelectBoxComponent({
	name,
	label,
	labelClass = "dx-field-item-label-text",
	placeholder,
	tabIndex,
	readOnly = false,
	disabled = false,
	visible = true,
	grouped,
	validationRules,
	items,
	dataSource,
	displayExpr = "Name",
	valueExpr = "Id",
	value,
	searchModeOption = "contains",
	searchExprOption = displayExpr,
	minSearchLengthOption = 0,
	searchEnabled = true,
	showClearButton = false,
	width,
	elementAttr = { class: "select-lg" },
	className,
	stylingMode = "outlined",
	deferRendering,
	infoPopOverMessage,
	infoPopOverClassName = "mt-2",
	infoPopOverWidth,
	validationMessageMode = "always",
	sortByProperty,
	sortDirection = "asc",
	onValueChanged,
	onSelectionChanged,
	onContentReady,
	saveSelectBoxInstance,
}: Readonly<ItiSelectBoxProps>) {
	const { isRequired } = useValidationRules(validationRules);

	const sortedItems = useMemo(() => {
		if (!items || !sortByProperty) {
			return items;
		}

		return [...items].sort((a, b) => {
			const valueA =
				typeof a[sortByProperty] === "string"
					? a[sortByProperty].toLowerCase()
					: a[sortByProperty];
			const valueB =
				typeof b[sortByProperty] === "string"
					? b[sortByProperty].toLowerCase()
					: b[sortByProperty];

			if (valueA === undefined || valueA === null)
				return sortDirection === "asc" ? 1 : -1;
			if (valueB === undefined || valueB === null)
				return sortDirection === "asc" ? -1 : 1;

			if (typeof valueA === "string" && typeof valueB === "string") {
				return sortDirection === "asc"
					? valueA.localeCompare(valueB)
					: valueB.localeCompare(valueA);
			}

			return sortDirection === "asc"
				? valueA > valueB
					? 1
					: valueA < valueB
						? -1
						: 0
				: valueA < valueB
					? 1
					: valueA > valueB
						? -1
						: 0;
		});
	}, [items, sortByProperty, sortDirection]);

	return (
		<>
			{label && !ItiString.isNullOrWhiteSpace(label) && (
				<div className="d-flex align-items-center">
					<div className={labelClass}>{label + (isRequired ? "*" : "")}</div>
					{infoPopOverMessage && (
						<InfoPopOver
							message={infoPopOverMessage}
							classNameInfo={infoPopOverClassName}
							widthPopover={infoPopOverWidth}
						/>
					)}
				</div>
			)}

			<DxSelectBox
				name={name}
				placeholder={placeholder}
				tabIndex={tabIndex}
				readOnly={readOnly}
				disabled={disabled}
				visible={visible}
				grouped={grouped}
				dataSource={dataSource ?? sortedItems}
				elementAttr={elementAttr}
				displayExpr={displayExpr}
				valueExpr={valueExpr}
				searchMode={searchModeOption}
				searchExpr={searchExprOption}
				minSearchLength={minSearchLengthOption}
				searchEnabled={searchEnabled}
				showClearButton={showClearButton}
				width={width}
				className={className}
				stylingMode={stylingMode}
				deferRendering={deferRendering}
				value={value}
				validationMessageMode={validationMessageMode}
				onValueChanged={(e: ValueChangedEvent) => {
					if (onValueChanged) {
						onValueChanged(e);
					}
				}}
				onSelectionChanged={(e: SelectionChangedEvent) => {
					if (onSelectionChanged) {
						onSelectionChanged(e);
					}
				}}
				onContentReady={onContentReady}
				onInitialized={saveSelectBoxInstance}
			>
				<DxValidator validationRules={validationRules} />
			</DxSelectBox>
		</>
	);
}
